/**
 * SignUpFormRedesign - Documentary Editorial aesthetic
 *
 * Warm, authentic sign-up form matching the WEattitude.org
 * landing page redesign.
 */

import { Form, useFormikContext } from "formik";
import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { APP_ROUTES } from "const";
import { FormikInput } from "components";
import AuthPanelRedesign from "components/AuthPanel/AuthPanelRedesign";
import { FIELD_NAMES, MAX_FORM_FIELDS_LENGTH } from "./SignUpForm.constants";

interface ValuesType {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Custom styled button for the editorial aesthetic
function EditorialButton({
  children,
  disabled,
  type = "button",
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type={type}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative w-full py-3.5 px-6 rounded-lg font-medium text-base
        transition-all duration-300 ease-out
        ${disabled
          ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
          : 'text-white cursor-pointer'
        }
      `}
      style={{
        backgroundColor: disabled ? undefined : (isHovered ? '#A13508' : '#C2410C'),
        fontFamily: "'Source Sans 3', sans-serif",
        boxShadow: disabled ? 'none' : (
          isHovered
            ? '0 10px 30px -5px rgba(194, 65, 12, 0.4)'
            : '0 4px 15px -3px rgba(194, 65, 12, 0.3)'
        ),
        transform: isHovered && !disabled ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      {children}
    </button>
  );
}

// Custom styled input wrapper for editorial aesthetic
function EditorialInputWrapper({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        className="block text-sm font-medium tracking-wide"
        style={{
          color: '#44403C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        {label}
      </label>
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

function SignUpFormRedesign() {
  const { isSubmitting } = useFormikContext<ValuesType>();

  return (
    <AuthPanelRedesign variant="signup">
      {/* Headline */}
      <h1
        className="mt-6 text-3xl lg:text-4xl leading-tight"
        style={{
          color: '#1C1917',
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
        }}
      >
        Join the movement
      </h1>
      <p
        className="mt-3 text-base"
        style={{
          color: '#78716C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        Create an account to start making an impact in communities worldwide.
      </p>

      {/* Sign in link */}
      <div
        className="mt-4 text-sm"
        style={{
          color: '#78716C',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        Already have an account?{' '}
        <Link
          to={APP_ROUTES.SIGN_IN}
          className="font-semibold transition-colors hover:underline"
          style={{ color: '#C2410C' }}
        >
          Sign in
        </Link>
      </div>

      {/* Form */}
      <Form className="mt-8 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <EditorialInputWrapper label="First name">
            <FormikInput
              autoComplete="given-name"
              maxLength={MAX_FORM_FIELDS_LENGTH}
              name={FIELD_NAMES.FIRST_NAME}
              placeholder="Ada"
              className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
              style={{ fontFamily: "'Source Sans 3', sans-serif" }}
            />
          </EditorialInputWrapper>

          <EditorialInputWrapper label="Last name">
            <FormikInput
              autoComplete="family-name"
              maxLength={MAX_FORM_FIELDS_LENGTH}
              name={FIELD_NAMES.LAST_NAME}
              placeholder="Okafor"
              className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
              style={{ fontFamily: "'Source Sans 3', sans-serif" }}
            />
          </EditorialInputWrapper>
        </div>

        <EditorialInputWrapper label="Email address">
          <FormikInput
            autoComplete="email"
            name={FIELD_NAMES.EMAIL}
            placeholder="you@example.com"
            className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />
        </EditorialInputWrapper>

        <EditorialInputWrapper label="Create a password">
          <FormikInput
            autoComplete="new-password"
            maxLength={MAX_FORM_FIELDS_LENGTH}
            name={FIELD_NAMES.PASSWORD}
            type="password"
            placeholder="••••••••"
            className="!rounded-[6px] !border-0 !bg-white !px-4 !py-3 focus:!border-[#C2410C] focus:!ring-[#C2410C]/20 !transition-all !w-full"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />
        </EditorialInputWrapper>

        {/* Password hint */}
        <p
          className="text-xs"
          style={{
            color: '#A8A29E',
            fontFamily: "'Source Sans 3', sans-serif",
          }}
        >
          Use 8+ characters with a mix of letters, numbers & symbols
        </p>

        {/* Submit button */}
        <div className="pt-2">
          <EditorialButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </EditorialButton>
        </div>
      </Form>

      {/* Terms notice */}
      <p
        className="mt-6 text-xs text-center leading-relaxed"
        style={{
          color: '#A8A29E',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        By creating an account, you agree to our{' '}
        <a href="/terms" className="underline hover:text-stone-600">Terms of Service</a>
        {' '}and{' '}
        <a href="/privacy" className="underline hover:text-stone-600">Privacy Policy</a>
      </p>

    </AuthPanelRedesign>
  );
}

export default memo(SignUpFormRedesign);
